import {
  defaultInventoryCopy,
  formatAmountWithUnit,
  INVENTORY_TABS,
  type InventoryTab,
  isInventoryTab,
  isSellable,
  SELL_MAX_ITEMS,
  type StorefrontTerminology,
  sellBackCents,
  WITHDRAWAL_ISSUE_LABEL,
  withdrawalIssueText,
} from "@loot-os/api-contract";
import type { InventoryItem } from "@loot-os/client";
import type {
  InventoryContent,
  InventoryGridState,
  InventoryUnit,
} from "@loot-os/design-system/templates/inventory/inventoryContent";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useCurrency } from "../../../lib/currency.js";
import { inventoryInfiniteQueryOptions } from "../../../lib/queries.js";
import { useShopper } from "../../../lib/shopper.js";
import { useTerminology } from "../../../lib/terminology.js";
import {
  useAccountHeaderContent,
  useAccountTabsContent,
  useLootMe,
} from "../shared/accountHeader.js";

/** Why a unit can't ship: "Can't ship" on its card, opening the full sentence. */
function withdrawalIssue(
  item: InventoryItem,
  terms: StorefrontTerminology,
): InventoryUnit["issue"] {
  const text = withdrawalIssueText(item, terms);
  return text ? { label: WITHDRAWAL_ISSUE_LABEL, text } : undefined;
}

/**
 * GET /v1/me/inventory and POST /v1/me/inventory/sell — the units the shopper owns after a
 * claim, and selling them back to the house for their value. Withdrawing one is the tenant's own
 * server's call (`POST /v1/fulfillment/orders`), so there is no withdraw button here. Returns the
 * page's content and the units the sale dialog confirms. The words are the store's default copy
 * (`defaultInventoryCopy`); what a unit can do and why it can't ship are the platform's
 * (`inventoryUnits.ts`), and so are the load error and the sale's dialog.
 */
export function useInventoryPage(): {
  content: InventoryContent;
  sale: {
    open: boolean;
    setOpen: (open: boolean) => void;
    items: InventoryItem[];
    onSold: () => void;
  };
} {
  const { sessionToken } = useShopper();
  const currency = useCurrency();
  const terms = useTerminology();
  const copy = defaultInventoryCopy(terms);
  const me = useLootMe();
  const header = useAccountHeaderContent(me);
  const tabs = useAccountTabsContent("vault");
  const [status, setStatus] = useState<InventoryTab>("owned");
  const [selected, setSelected] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);

  const query = useInfiniteQuery({
    ...inventoryInfiniteQueryOptions(status),
    enabled: !!sessionToken,
  });
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);
  const loadError = query.error
    ? query.error instanceof Error
      ? query.error.message
      : `failed to load your ${terms.inventory.singular}`
    : null;

  const sellable = useMemo(() => items.filter(isSellable), [items]);
  // A sale, a refetch or a tab change can take a unit off the page; the selection follows what
  // is actually shown rather than keeping ids the shopper can no longer see.
  const picked = useMemo(
    () => sellable.filter((item) => selected.includes(item.id)),
    [sellable, selected],
  );
  const pickedCents = sellBackCents(picked);
  // One sale carries at most SELL_MAX_ITEMS units, so the selection stops there instead of
  // building one the route would refuse whole.
  const selectionLimit = Math.min(sellable.length, SELL_MAX_ITEMS);
  const allPicked = selectionLimit > 0 && picked.length === selectionLimit;

  const toggle = (id: string) =>
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= SELL_MAX_ITEMS) return prev;
      return [...prev, id];
    });

  const unit = (item: InventoryItem): InventoryUnit => ({
    id: item.id,
    name: item.item.name,
    imageUrl: item.item.imageUrl,
    note:
      item.status === "sold"
        ? { kind: "text", text: copy.vault.notes.sold }
        : item.status === "fulfillment_requested"
          ? { kind: "text", text: copy.vault.notes.shipping }
          : item.status === "withdrawn"
            ? { kind: "text", text: copy.vault.notes.shipped }
            : item.item.value > 0
              ? { kind: "value", cents: item.item.value }
              : { kind: "text", text: copy.vault.notes.noValue },
    issue: withdrawalIssue(item, terms),
    faded: item.status === "sold",
    // A sellable unit toggles its selection; anything else is static, because there is nothing
    // to do with a unit already sold or already shipping.
    selection: isSellable(item)
      ? { selected: selected.includes(item.id), onToggle: () => toggle(item.id) }
      : undefined,
  });

  const state: InventoryGridState =
    items.length > 0
      ? { status: "ready", units: items.map(unit) }
      : loadError
        ? { status: "error", message: loadError }
        : query.isPending
          ? { status: "loading" }
          : {
              status: "empty",
              message: copy.vault.status.empty[status],
              action:
                status === "owned"
                  ? { label: copy.vault.browse, href: "/boxes", emphasis: "primary" }
                  : null,
            };

  const content: InventoryContent = {
    header,
    tabs,
    vault: {
      title: copy.vault.title,
      description: copy.vault.description,
      currency,
      status: {
        label: copy.vault.status.label,
        value: status,
        options: INVENTORY_TABS.map((tab) => ({
          value: tab,
          label: copy.vault.status.options[tab],
        })),
        onValueChange: (next) => {
          if (!isInventoryTab(next)) return;
          setStatus(next);
          setSelected([]);
        },
      },
      selectable:
        status === "owned" && sellable.length > 0
          ? {
              summary: copy.vault.selectable.summary({
                sellable: sellable.length,
                shown: items.length,
                limit: SELL_MAX_ITEMS,
              }),
              toggleAll: {
                label: allPicked
                  ? copy.vault.selectable.clearSelection
                  : copy.vault.selectable.selectAll,
                onClick: () =>
                  setSelected(
                    allPicked ? [] : sellable.slice(0, SELL_MAX_ITEMS).map((item) => item.id),
                  ),
              },
            }
          : null,
      state,
      error: items.length > 0 && loadError ? loadError : undefined,
      paging: {
        hasMore: query.hasNextPage,
        loading: query.isFetchingNextPage,
        onLoadMore: () => void query.fetchNextPage(),
        label: copy.vault.paging.loadMore,
        status: copy.vault.paging.showing,
      },
    },
    selection:
      picked.length > 0
        ? {
            summary: copy.selection.summary(picked.length),
            totalCents: pickedCents,
            totalLabel: copy.selection.totalLabel,
            clear: { label: copy.selection.clear, onClick: () => setSelected([]) },
            sell: {
              label: copy.selection.sell(formatAmountWithUnit(pickedCents, currency)),
              onClick: () => setConfirming(true),
            },
          }
        : undefined,
  };

  return {
    content,
    sale: {
      open: confirming,
      setOpen: setConfirming,
      items: picked,
      onSold: () => setSelected([]),
    },
  };
}
